import {
    Box, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Divider, Chip, Stack
} from "@mui/material";
import { AppId } from "../../../../app/appTypes";
import { selectStructureNodeById } from "../../../device/structureSlice";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useEffect, useMemo, useState } from "react";
import { Localized, useLocalization } from "@fluent/react";
import { useNavigate } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { alpha } from "@mui/material/styles";
import { CreateClientDocumentTemplateDialog } from "./CreateClientDocumentTemplateDialog";
import { DocumentHeader } from "../../documentsApi";
import { loadClientDocumentTemplates } from "../documentTemplateApi";
import { LpField } from "../../../../app/components/LpField";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { DocumentTypes } from "../../documentTypes";

interface StructureDetailsWindowProps {
    structureId: AppId;
}

export const StructureTemplatesView = ({ structureId }: StructureDetailsWindowProps) => {
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [templates, setTemplates] = useState<DocumentHeader[]>([]);
    const [createTemplateDialogVisible, setCreateTemplateDialogVisible] = useState(false);
    const structure = useAppSelector((state) => selectStructureNodeById(state, structureId));

    useEffect(() => {
        if (templates.length === 0) {
            dispatch(loadClientDocumentTemplates()).then(unwrapResult).then(setTemplates);
        }
    }, [dispatch, templates.length]);

    const getNodeById = useAppSelector(
        (state) => (id: AppId | number | null | undefined) =>
            id != null ? selectStructureNodeById(state, id as AppId) : undefined
    );

    const redirectToDocumentTemplate = (id: AppId, structureId: AppId, type: DocumentTypes) => {
        let url: string;

        switch (type) {
            // case "URS":
            //     url = `/sys/urstemplates/edit/${id}?structureId=${structureId}`;
            //     break;

            default:
                url = `/client-document-templates/edit/${id}?structureId=${structureId}`;
                break;
        }

        navigate(url);
    };

    const handleCancel = () => setCreateTemplateDialogVisible(false);
    const handleCreateNewTemplateClick = () => setCreateTemplateDialogVisible(true);

    const ancestorIds = useMemo(() => {

        const ids: number[] = [];
        let node = getNodeById(structureId) as any;
        for (let i = 0; i < 512 && node?.parentStructureId; i++) {
            ids.push(Number(node.parentStructureId));
            node = getNodeById(node.parentStructureId);
        }
        return ids;
    }, [structureId, getNodeById]);

    const ownRows = useMemo(
        () => templates.filter(t => Number(t.structureId) === Number(structureId)),
        [templates, structureId]
    );

    const inheritedRows = useMemo(
        () => templates.filter(t =>
            Number(t.structureId) !== Number(structureId)
            && ancestorIds.includes(Number(t.structureId))
        ),
        [templates, structureId, ancestorIds]
    );

    return (
        <>
            <Box>
                <Stack>
                    <Box
                        sx={{
                            bgcolor: "grey.100",
                            px: 2,
                            py: 1.5,
                            borderBottom: 1,
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="h6" fontWeight={700}>
                            <Localized id="menu-document-templates">_templates</Localized>
                        </Typography>
                    </Box>
                    <SelectedStructureBar
                        name={structure?.name} name_EN={structure?.name_EN} code={structure?.code} />
                </Stack>
            </Box>

            <Divider />

            <SectionTable
                title={l10n.getString("document-template-for-structure", null, "_dedicated")}
                rows={ownRows}
                structureId={structureId}
                onOpen={redirectToDocumentTemplate}
                emptyText={l10n.getString("document-template-empty-list", null, "_empty")}
            />
            <Box p={2} display="flex" justifyContent="center">
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleCreateNewTemplateClick}
                >
                    <Localized id="document-template-create">_Utwórz nowy szablon</Localized>
                </Button>
            </Box>
            <Divider />
            <SectionTable
                title={l10n.getString("document-template-inherited-list", null, "_Inherited templates")}
                rows={inheritedRows}
                structureId={structureId}
                onOpen={redirectToDocumentTemplate}
                emptyText={l10n.getString("document-template-inherited-list-empty", null, "_No inherited templates.")}
                showOrigin
                resolveStructure={(id) => getNodeById(id)}
            />
            {createTemplateDialogVisible && (
                <CreateClientDocumentTemplateDialog
                    defaultStructureId={structureId}
                    processId={null}
                    defaultType={"IQ"}
                    onSucces={(_doc: DocumentHeader) => {
                        dispatch(loadClientDocumentTemplates()).then(unwrapResult).then(setTemplates);
                        setCreateTemplateDialogVisible(false);
                    }}
                    onCancel={handleCancel}
                />
            )}
        </>
    );
};


function SectionTable({
    title,
    rows,
    structureId,
    onOpen,
    emptyText,
    showOrigin = false,
    resolveStructure,
}: {
    title?: string;
    rows: DocumentHeader[];
    structureId: AppId;
    onOpen: (id: AppId, structureId: AppId, type: DocumentTypes) => void;
    emptyText: string;
    showOrigin?: boolean;
    resolveStructure?: (id: AppId) => { name?: string; name_EN?: string; code?: string } | undefined;
}) {
    const totalCols = 6 + (showOrigin ? 1 : 0);
    return (
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>

            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={totalCols} sx={{ bgcolor: "#f9f9f9", fontWeight: "bold" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <FactCheckIcon fontSize="medium" sx={{ mr: 1, color: "text.secondary" }} />
                                <Localized id="">
                                    <span>{title}</span>
                                </Localized>
                            </Box>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell align="left" sx={{ width: 80 }}>
                            <Localized id="lp">_lp</Localized>
                        </TableCell>
                        <TableCell align="left">
                            <Localized id="document-number">_no</Localized>
                        </TableCell>
                        <TableCell align="left">
                            <Localized id="title">_title</Localized>
                        </TableCell>
                        <TableCell align="left">
                            <Localized id="type">_type</Localized>
                        </TableCell>
                        <TableCell align="left">
                            <Localized id="document-language">_language</Localized>
                        </TableCell>
                        {showOrigin && <TableCell align="left">
                            <Localized id="inherited-from">_inherited-from</Localized>
                        </TableCell>}
                        <TableCell align="right" sx={{ width: 120 }} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => {
                        const origin = showOrigin && resolveStructure
                            ? resolveStructure(row.structureId)
                            : undefined;

                        return (
                            <TableRow key={row.id} hover>
                                <TableCell align="left"><LpField value={index + 1} /></TableCell>
                                <TableCell align="left">{row.no}</TableCell>
                                <TableCell align="left">
                                    {(row as any).name ?? (row as any).title ?? "-"}
                                </TableCell>
                                <TableCell align="left">{row.type}</TableCell>
                                <TableCell align="left">{row.language}</TableCell>

                                {showOrigin && (
                                    <TableCell align="left">
                                        {origin
                                            ? <>
                                                {origin.name_EN ? `${origin.name_EN} (${origin.name})` : "—"}
                                                {origin.code ? <Chip size="small" label={origin.code} sx={{ ml: 1 }} /> : null}
                                            </>
                                            : "—"}
                                    </TableCell>
                                )}

                                <TableCell align="right">
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        onClick={() => onOpen(row.id, structureId, row.type as DocumentTypes)}
                                    >
                                        <Localized id="go-to">_Przejdź</Localized>
                                    </Button>
                                </TableCell>
                            </TableRow>
                        );
                    })}

                    {rows.length === 0 && !!emptyText && (
                        <TableRow>
                            <TableCell
                                colSpan={showOrigin ? 7 : 6}
                                align="center"
                                sx={{ py: 6, color: "text.secondary" }}
                            >
                                {emptyText}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

const SelectedStructureBar = ({ name, name_EN, code }: { name?: string; name_EN?: string; code?: string }) => (
    <Box
        sx={(theme) => ({
            bgcolor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            px: 2,
            py: 1.25,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
        })}
    >
        <AccountTreeIcon fontSize="small" sx={{ color: "info.main" }} />
        <Typography variant="subtitle2" color="info.main">
            <Localized id="structure">_structure:</Localized>:
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {name_EN} ({name})
        </Typography>
        {code ? <Chip label={code} variant="outlined" /> : null}
    </Box>
);